// @flow
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '@finect/tabular-core/Button';
import { Helmet } from 'react-helmet-async';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { Side } from '@finect/tabular-core/Side';
import { ChevronDown } from '@finect/tabular-icons/chevronDown';
import { Spinner } from '@finect/tabular-components/Spinner';
import { LoadingBar } from '@finect/tabular-components/LoadingBar';
import { ContentState, convertToRaw } from 'draft-js';
import type { Editor } from 'draft-js';
import { Alert } from '@finect/tabular-components/Alert';
import { BottomAds } from '@finect/tabular-components/Ads';
import { LiveBanner } from '@finect/tabular-components/LiveBanner';
import type { WithParamsProps } from '@finect/front-core/withParams';
import { withParams } from '@finect/front-core/withParams';
import { Datalayer } from '@finect/front-gtm';
import { withSubscriptions } from '@finect/front-core/User';
import type { WithSubscriptionsProps } from '@finect/front-core/User';
import type { QuestionType, UserType, TagType } from '../../types';

import {
  Disclaimer,
  QuestionPreview,
  FilterSelectBlock,
  FilterSelect,
  CurrentQuestions,
  QuestionFilters,
  QuestionDetail,
  QuestionDetailInner,
  TabMenu,
  NewQuestionBlock,
  NewQuestion,
  ShowMoreBlock,
  ShowMore,
  NotFollowedTags,
  RandomTags
} from '../../components';
import { processTags } from '../../utils';

import { getQuestions, voteQuestion, preFetchedQuestions } from '../../store/questions/actions';

import { getTags } from '../../store/tags/actions';
import { openOverlay } from '../../store/overlay/actions';

import { PendingModerationBlock, MainContent, SpinnerBlock, ButtonContainer } from './partials';

const mapStateToProps = (state: Object) => ({
  user: state.user.user,
  tags: state.tags,
  questions: state.questions,
  // savedQuestions: state.user.subscriptions.questions,
  likedQuestions: state.user.likes.questions
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      preFetchedQuestions,
      getQuestions,
      voteQuestion,
      getTags,
      openOverlay
    },
    dispatch
  )
});

type Props = {
  type?: string,
  user: UserType,
  staticContext?: Object,
  actions: Object,
  questions: {
    items: { [key: string]: QuestionType },
    list: Array<string>,
    more: boolean,
    type: string,
    isTag: number
  },
  tags: {
    [key: number]: TagType
  },
  location: Object,
  history: Object,
  // savedQuestions: Array<string>,
  likedQuestions: Array<string>
} & WithParamsProps &
  WithSubscriptionsProps;

type State = {
  fetchingMore: boolean,
  listEnd: boolean,
  loading: boolean,
  previousPath: string
};

const METAS = {
  latest: {
    title: 'Últimas - Preguntas Finect',
    canonical: '/preguntas/ultimas',
    description:
      'Descubre las últimas preguntas sobre finanzas e inversión en Finect o escribe tus propias dudas para que sean respondidas por la comunidad.'
  },
  active: {
    title: 'Preguntas Finect',
    canonical: '/preguntas/mas-activas',
    description:
      '¿Tienes dudas sobre tus finanzas? Respondemos todas las dudas sobre inversión en Finect. ¡Deja tus preguntas para que sean respondidas!'
  }
  // mine: {
  //   title: 'Mis preguntas - Finect',
  //   description: ''
  // }
  // followedQuestions: {
  //   title: 'Preguntas guardadas - Finect',
  //   description: ''
  // }
};
class QuestionList extends React.Component<Props, State> {
  titleEditor: Editor;
  // TODO Change any for HTMLElement, and fix Flow...
  filterSelect: any;

  state = {
    fetchingMore: false,
    listEnd: false,
    loading: false,
    previousPath: ''
  };

  static loadInitialData(store, cookie, match, params) {
    return store.dispatch(getQuestions(params ? params.type : '', null, null, cookie));
  }

  componentDidMount() {
    const { questions, type, history, actions } = this.props;
    const { forceFetch } = history.location.state || {};

    if (questions.type !== type || forceFetch) {
      this.setState({ loading: true });
      actions.getQuestions(type);
    }
    if (!Object.keys(this.props.tags).length) {
      actions.getTags();
    }
  }

  /* eslint-disable */
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // Fetch new questions
    if (this.props.type !== nextProps.type) {
      this.props.actions.getQuestions(nextProps.type);
      this.setState({
        listEnd: false,
        loading: true,
        previousPath: this.props.location.pathname.replace(/\/\s*$/, ""),
      });
    }

    // Pagination
    if (this.state.fetchingMore) {
      const newState = {};
      newState.fetchingMore = false;
      if (!nextProps.questions.more) newState.listEnd = true;
      this.setState(newState);
    }

    // Stop loader
    const { type } = this.props.questions;
    const { loading } = this.state;
    const { forceFetch } = this.props.history.location.state || {};
    if ((type !== nextProps.questions.type && loading) || (forceFetch && loading)) {
      this.setState({ loading: false, previousPath: "" });
    }
  }
  /* eslint-enable */

  pagination = () => {
    const offset = this.props.questions.list.length;
    this.props.actions.getQuestions(this.props.type, null, offset);
    this.setState({ fetchingMore: true });
  };

  focusTitleEditor = () => {
    this.titleEditor.focus();
  };

  handleReturn = (value: string) => {
    localStorage.setItem(
      'questionTitle',
      JSON.stringify({
        content: JSON.stringify(convertToRaw(ContentState.createFromText(value))),
        timestamp: new Date().getTime()
      })
    );
    this.props.history.push('/preguntas/nueva-pregunta');
  };

  handleNewQuestion = () => {
    this.props.history.push('/preguntas/nueva-pregunta');
  };

  handleChange = () => {
    this.props.history.push(this.filterSelect.value);
  };

  handleLike = (questionId: string) => {
    const { handleEntityFollow, likedQuestions, user, actions } = this.props;

    if (user.isLoggedIn) {
      handleEntityFollow('question', 'like', questionId, !likedQuestions.includes(questionId));
      return actions.voteQuestion(questionId, !likedQuestions.includes(questionId));
    }
    const blob = {
      copy: 'Accede a Finect para realizar esta acción.',
      action: 'like',
      target: {
        id: questionId,
        type: 'question'
      }
    };
    actions.openOverlay(blob);
  };

  // handleFollow = (questionId: string) => {
  //   const { handleEntityFollow, savedQuestions, user, actions } = this.props;

  //   if (user.isLoggedIn) {
  //     return handleEntityFollow('question', 'subscribe', questionId, !savedQuestions.includes(questionId));
  //   }
  //   const blob = {
  //     copy: 'Accede a Finect para realizar esta acción.',
  //     action: 'like',
  //     target: {
  //       id: questionId,
  //       type: 'question'
  //     }
  //   };
  //   actions.openOverlay(blob);
  // };

  render() {
    // Should always receive the same payload Array or Object
    const { user, tags, questions, actions, type, /*savedQuestions = [],*/ likedQuestions = [] } = this.props;
    const { loading, previousPath } = this.state;
    const { pathname } = this.props.location;
    const path = pathname.replace(/\/\s*$/, '');
    const currentPath = loading ? previousPath : path;
    const hasTags = Object.keys(tags).length !== 0;
    const hasQuestions = Boolean(questions.list.length);
    const loadedQuestions = questions.type;

    const { moderation } = this.props.params;

    const filters = [
      { name: 'Últimas', path: '/preguntas/ultimas' },
      { name: 'Más activas', path: '/preguntas/mas-activas', nofollow: true }
    ];

    const privateFilters = [
      // { name: 'Guardadas', path: '/preguntas/guardadas', nofollow: true },
      // {
      //   name: 'Mis preguntas',
      //   path: '/preguntas/mis-preguntas',
      //   nofollow: true
      // }
    ];

    const currentFilters = user.isLoggedIn ? [...filters, ...privateFilters] : [...filters];

    let followedTags = [];
    let followedTagsNames = [];
    let relatedTags = {};

    if (hasTags) {
      const allTags = processTags(tags);
      followedTags = allTags.followed;
      followedTagsNames = allTags.followedNames;
      relatedTags = allTags.filtered;
    }

    let whiteState;
    if (!hasQuestions) {
      switch (currentPath) {
        // case '/preguntas/guardadas':
        //   if (loadedQuestions === 'followedQuestions') whiteState = <NotFollowedQuestions />;
        //   break;
        case '/preguntas/mis-temas':
          if (loadedQuestions === 'followedTags') {
            whiteState = <NotFollowedTags />;
          }
          break;
        // case '/preguntas/mis-preguntas':
        //   if (loadedQuestions === 'mine') whiteState = <NotOwnQuestions />;
        //   break;
        default:
          break;
      }
    }

    const showFollowedTags =
      followedTags.length &&
      ((!loading && path === '/preguntas/mis-temas') || (loading && previousPath === '/preguntas/mis-temas'));

    return (
      <Container noPaddingQuery>
        <Helmet key={pathname}>
          <meta charSet="utf-8" />
          <title>{type ? METAS[type].title : ''}</title>
          <meta name="description" content={type ? METAS[type].description : ''} />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={type ? METAS[type].canonical : '/preguntas'} />
        </Helmet>
        {questions.list.length && !loading ? (
          <Datalayer id="Scene" type="ready" params={{ pageType: 'home', pageId: `questions-${type || 'latest'}` }} />
        ) : null}
        <Datalayer id="Scene" type="pageType" params={{ pageType: 'home', pageId: 'questions' }} />
        <LoadingBar loading={loading} />
        <LiveBanner topMargin />
        <ContainerInner>
          <MainContent noMargin>
            <NewQuestionBlock>
              <QuestionDetail prePublish>
                <QuestionDetailInner>
                  <NewQuestion
                    placeholder="¿Tienes una duda? Busca respuestas en Finect"
                    handleReturn={this.handleReturn}
                  />
                </QuestionDetailInner>
              </QuestionDetail>
            </NewQuestionBlock>
          </MainContent>
          <Side>
            <ButtonContainer>
              <Button onClick={this.handleNewQuestion} width="auto">
                Haz tu pregunta
              </Button>
            </ButtonContainer>
          </Side>
        </ContainerInner>
        <ContainerInner>
          <MainContent>
            {['pending'].includes(moderation) ? (
              <PendingModerationBlock>
                <Alert withIcon={false} type="success" title="¡Gracias por participar!">
                  Tu pregunta está pendiente de moderación.
                </Alert>
              </PendingModerationBlock>
            ) : null}
            <QuestionFilters>
              <TabMenu options={filters} path={currentPath} />
              {/* CAMBIAR A NUEVO ENDPOINT API V4*/}
              {user.isLoggedIn ? <TabMenu options={privateFilters} path={currentPath} /> : null}
            </QuestionFilters>
            <FilterSelectBlock>
              <ChevronDown size="20" />
              <FilterSelect
                onChange={this.handleChange}
                ref={title => (this.filterSelect = title)}
                value={currentPath}
              >
                {currentFilters.map((filter, index) => (
                  <option key={index} value={filter.path}>
                    {filter.name}
                  </option>
                ))}
              </FilterSelect>
            </FilterSelectBlock>
            <CurrentQuestions>
              {whiteState ? whiteState : null}
              {questions.list.map((id) => {
                const question = this.props.questions.items[id];
                return (
                  <QuestionPreview
                    key={id}
                    question={question}
                    voted={likedQuestions.includes(id)}
                    handleLike={() => this.handleLike(id)}
                    // following={savedQuestions.includes(id)}
                    // handleFollow={() => this.handleFollow(id)}
                    blockVote={question.author === user.id}
                    user={user}
                    reason={showFollowedTags ? followedTagsNames : null}
                  />
                );
              })}
              {questions.more && !this.state.fetchingMore ? (
                <ShowMoreBlock>
                  <ShowMore onClick={this.pagination}>Mostrar más preguntas</ShowMore>
                </ShowMoreBlock>
              ) : null}
              {this.state.fetchingMore ? (
                <SpinnerBlock>
                  <Spinner />
                </SpinnerBlock>
              ) : null}
              {this.state.listEnd ? <ShowMoreBlock light>Has llegado al final de la lista.</ShowMoreBlock> : null}
            </CurrentQuestions>

            <Disclaimer />
          </MainContent>
          <Side padding>
            {hasTags && !questions.isTag ? (
              <RandomTags
                title="Temas que pueden interesarte"
                hide="miniDesktop"
                tags={relatedTags}
                register={actions.preFetchedQuestions}
              />
            ) : null}
          </Side>
        </ContainerInner>
        <BottomAds path="Finect_Content_1" sticky closeable />
      </Container>
    );
  }
}

export default withSubscriptions(connect(mapStateToProps, mapDispatchToProps)(withParams(QuestionList)), [
  'profiles',
  'products'
]);
